import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import RecordWizard from '../../../reusable/formRecords/RecordWizards';
import { Field as FormField, Select as FormSelect, SectionHead, SectionDivider, ReadonlyBox, INP, INP_LOCKED } from '../../../reusable/formRecords/formFieldPrimitives';
import { FilterBar, SearchRow } from '../../../MarketTrad/shared';
import NlimsDataTable from '../../../shared/NlimsDataTable/NlimsDataTable';
import { useCountUp } from '../../../../hooks/useCountUp';
import { useAuth } from '../../../../context/AuthContext';
import { conflictsApi, buildCommitteePayload } from '../../../../api/conflictsApi';
import { ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, actionCellHtml, WORKFLOW_RULES } from '../../../shared/RowActionMenu';
import { useApplyScopeLock, useLocationOptions } from '../../../Admin/shared/useLocationOptions';
import { useCollectionSync } from '../../../../hooks/useCollectionSync';
import { useRegistryLabel } from '../../../Admin/FoundationalTables/FormRegistry/registryFieldOverrides';

const COMMITTEE_TYPES = [
  { value: 'state_cmc', label: 'State CMC' },
  { value: 'lga_cmc', label: 'LGA CMC' },
  { value: 'community_peace_committee', label: 'Community Peace Committee' },
  { value: 'traditional_council', label: 'Traditional Council' },
  { value: 'interfaith', label: 'Interfaith' },
  { value: 'security_anchored', label: 'Security Anchored' },
];

const MEETING_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'as_needed', label: 'As Needed' },
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'dissolved', label: 'Dissolved' },
];

const VIEW_STATUS_DOT = {
  draft: 'bg-gray-400',
  submitted: 'bg-blue-500',
  validated: 'bg-amber-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
};

const VIEW_STATUS_PILL = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  validated: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-600',
};

const BADGE_STYLE = {
  active: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  inactive: 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  suspended: 'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  dissolved: 'background:#fef3c7;color:#92400e;border:1px solid #fcd34d;',
  weekly: 'background:#dbeafe;color:#1d4ed8;border:1px solid #93c5fd;',
  monthly: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  quarterly: 'background:#ede9fe;color:#6d28d9;border:1px solid #c4b5fd;',
  as_needed: 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function humanize(value) {
  return String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: '2-digit' });
}

function badgeHtml(data, type) {
  if (type !== 'display') return data ?? '';
  const raw = String(data ?? '').trim().toLowerCase();
  const style = BADGE_STYLE[raw] ?? 'background:#f3f4f6;color:#6b7280;';
  const label = humanize(data) || '—';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${label}</span>`;
}

function Field({ label, children, span2 = false, required = false, hint }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <FormField label={label} required={required} hint={hint}>
        {children}
      </FormField>
    </div>
  );
}

function Select({ label, value, onChange, options, span2 = false, required = false, readOnly = false, placeholder = 'Select...', hint }) {
  return (
    <Field label={label} span2={span2} required={required} hint={hint}>
      {readOnly ? (
        <ReadonlyBox>{value || 'Not set'}</ReadonlyBox>
      ) : (
        <FormSelect
          value={value ?? ''}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          className={INP}
        />
      )}
    </Field>
  );
}

function TextInput({ label, value, onChange, type = 'text', span2 = false, readOnly = false, placeholder = '', max, required = false, hint }) {
  return (
    <Field label={label} span2={span2} required={required} hint={hint}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        max={max}
        className={readOnly ? INP_LOCKED : INP}
      />
    </Field>
  );
}

function StatCard({ end, isPercent = false, suffix, label, accent, accentColor = 'green' }) {
  const { count, ref } = useCountUp(end, 1800);
  const displayed = isPercent ? `${count}%` : count.toLocaleString('en-NG');

  return (
    <div ref={ref} className="bg-gray-50 rounded-xl p-5 flex-1 min-w-0">
      <p className="text-[26px] font-extrabold text-gray-900 leading-tight tabular-nums">
        {displayed}
        {suffix && <span className="text-[26px] font-extrabold text-gray-900"> {suffix}</span>}
      </p>
      <p className="text-[12px] text-gray-400 mt-1 leading-snug">{label}</p>
      <p className={`text-[11px] font-medium mt-1 leading-snug ${accentColor === 'green' ? 'text-green-500' : 'text-gray-400'}`}>
        {accent}
      </p>
    </div>
  );
}

function CommitteeStatCards({ committees }) {
  const total = committees.length;
  const active = committees.filter((item) => String(item.status ?? '').toLowerCase() === 'active').length;
  const recentMeeting = committees.filter((item) => {
    const lastMeeting = item.last_meeting_date ?? item.lastMeetingDate;
    if (!lastMeeting) return false;
    const date = new Date(lastMeeting);
    if (Number.isNaN(date.getTime())) return false;
    const days = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
    return days <= 90;
  }).length;
  const averageResolution =
    total > 0
      ? Math.round(
          committees.reduce((sum, item) => {
            const handled = Number(item.cases_handled ?? item.casesHandled ?? 0);
            const resolved = Number(item.cases_resolved ?? item.casesResolved ?? 0);
            return sum + (handled > 0 ? (resolved / handled) * 100 : 0);
          }, 0) / total
        )
      : 0;

  return (
    <div className="flex gap-4 mb-6">
      <StatCard end={total} suffix="Committees" label="Total Conflict Committees" accent="Live committee registry" accentColor="green" />
      <StatCard end={active} suffix="Active" label="Functional Committees" accent={`${total ? Math.round((active / total) * 100) : 0}% active`} accentColor="green" />
      <StatCard end={recentMeeting} suffix="Committees" label="Recent Meetings" accent="Meetings within the last 90 days" accentColor="green" />
      <StatCard end={averageResolution} isPercent suffix="Rate" label="Average Resolution Rate" accent="Resolved cases / handled cases" accentColor="gray" />
    </div>
  );
}

export function CommitteeEditorModal({ mode, row, onClose, onSaved, stateOptions, isFieldUser, modulePanel = null, onEdit, canEdit = true }) {
  const [form, setForm] = useState({
    committeeName: '',
    committeeType: '',
    state: '',
    lga: '',
    wardCommunity: '',
    establishmentDate: '',
    chairperson: '',
    meetingFrequency: 'monthly',
    status: 'active',
    totalMembers: '',
    trainedMembers: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const isReviewStep = step === 1;
  const { lgaOptions, wardOptions, loadingLgas, loadingWards } = useLocationOptions(form.state, form.lga);
  const { lockedState, lockedLga } = useApplyScopeLock(form, setForm);
  const currentDataStatus = (row?.data_status ?? row?.dataStatus ?? 'draft').toLowerCase();
  const totalMembers = Number(form.totalMembers);
  const trainedMembers = Number(form.trainedMembers);
  const establishmentDateValid = !form.establishmentDate || form.establishmentDate <= todayISO();
  const canGoNext =
    form.committeeName.trim().length >= 5 &&
    Boolean(form.committeeType) &&
    Boolean(form.state) &&
    Boolean(form.lga) &&
    Boolean(form.wardCommunity.trim()) &&
    Boolean(form.establishmentDate) &&
    establishmentDateValid &&
    form.chairperson.trim().length >= 3 &&
    Boolean(form.meetingFrequency) &&
    Boolean(form.status) &&
    Number.isFinite(totalMembers) &&
    totalMembers >= 1 &&
    (form.trainedMembers === '' || (Number.isFinite(trainedMembers) && trainedMembers <= totalMembers));

  useEffect(() => {
    if (row) {
      setForm({
        committeeName: row.committee_name ?? row.committeeName ?? '',
        committeeType: row.committee_type ?? row.committeeType ?? '',
        state: row.state ?? '',
        lga: row.lga ?? '',
        wardCommunity: row.ward_community ?? row.wardCommunity ?? '',
        establishmentDate: row.established_date ?? row.establishedDate ?? '',
        chairperson: row.chairperson ?? '',
        meetingFrequency: row.meeting_frequency ?? row.meetingFrequency ?? 'monthly',
        status: row.status ?? 'active',
        totalMembers: String(row.total_members ?? row.totalMembers ?? ''),
        trainedMembers: String(row.trained_members ?? row.trainedMembers ?? ''),
      });
      return;
    }

    setForm({
      committeeName: '',
      committeeType: '',
      state: '',
      lga: '',
      wardCommunity: '',
      establishmentDate: '',
      chairperson: '',
      meetingFrequency: 'monthly',
      status: 'active',
      totalMembers: '',
      trainedMembers: '',
    });
  }, [row, mode]);

  const setValue = useCallback((key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'state') {
        next.lga = '';
        next.wardCommunity = '';
      }
      if (key === 'lga') {
        next.wardCommunity = '';
      }
      return next;
    });
  }, []);

  if (mode === 'view') {
    const dotCls = VIEW_STATUS_DOT[currentDataStatus] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[currentDataStatus] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {currentDataStatus}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Committee Registration" />
          <ReviewRow label="Committee Name" value={form.committeeName} />
          <ReviewRow label="Committee Type" value={form.committeeType} />
          <ReviewRow label="Chairperson" value={form.chairperson} />
          <ReviewRow label="Total Members" value={form.totalMembers} />
          <ReviewRow label="Trained Members" value={form.trainedMembers} />
          <ReviewRow label="Meeting Frequency" value={form.meetingFrequency} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="State" value={form.state} />
          <ReviewRow label="LGA" value={form.lga} />
          <ReviewRow label="Ward / Community" value={form.wardCommunity} />
          <ReviewRow label="Establishment Date" value={form.establishmentDate} />

          <SectionHead title="System" />
          <ReviewRow label="Status" value={form.status} />
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  const submit = async (opts = {}) => {
    if (saving) return;
    if (form.committeeName.trim().length < 5 || !form.committeeType || !form.state || !form.lga || !form.wardCommunity.trim() || !form.establishmentDate || form.chairperson.trim().length < 3) {
      setError('Committee name (min 5 chars), type, state, LGA, ward/community, chairperson (min 3 chars) and establishment date are required.');
      return;
    }
    if (form.establishmentDate > todayISO()) {
      setError('Establishment date cannot be in the future.');
      return;
    }
    const totalMembers = Number(form.totalMembers);
    if (!Number.isFinite(totalMembers) || totalMembers < 1) {
      setError('Total Members must be at least 1.');
      return;
    }
    if (form.trainedMembers !== '') {
      const trainedMembers = Number(form.trainedMembers);
      if (!Number.isFinite(trainedMembers) || trainedMembers > totalMembers) {
        setError('Trained Members must be a number less than or equal to Total Members.');
        return;
      }
    }

    const payload = buildCommitteePayload({
      committeeName: form.committeeName,
      committeeType: form.committeeType,
      state: form.state,
      lga: form.lga || null,
      wardCommunity: form.wardCommunity,
      establishmentDate: form.establishmentDate,
      chairperson: form.chairperson,
      meetingFrequency: form.meetingFrequency,
      totalMembers: form.totalMembers,
      trainedMembers: form.trainedMembers,
      status: form.status,
    });

    setSaving(true);
    setError('');
    try {
      if (mode === 'create') {
        await conflictsApi.committees.create(payload);
      } else if (row?.uuid) {
        await conflictsApi.committees.update(row.uuid, payload);
        if (opts.submitAfterSave) {
          await conflictsApi.committees.submit(row.uuid);
        }
      }
      toast.success(
        mode === 'create'
          ? 'Conflict committee created.'
          : (opts.submitAfterSave ? 'Conflict committee submitted for review.' : 'Conflict committee updated.'),
        { position: 'top-right', autoClose: 3000 }
      );
      await onSaved?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to save committee.';
      setError(msg);
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button onClick={() => setStep((current) => Math.max(0, current - 1))} className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
        Previous
      </button>
      {mode === 'edit' && isFieldUser ? (
        <>
          <button
            onClick={() => submit()}
            disabled={saving}
            className="h-11 flex-1 rounded-full border border-green-300 bg-white text-sm font-semibold text-green-700 hover:bg-green-50 disabled:opacity-60"
          >
            {saving ? 'Saving...' : (currentDataStatus === 'draft' ? 'Save as Draft' : 'Save Changes')}
          </button>
          <button
            onClick={() => submit({ submitAfterSave: true })}
            disabled={saving}
            className="h-11 flex-1 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? 'Submitting...' : 'Submit for Review'}
          </button>
        </>
      ) : (
        <button onClick={() => submit()} disabled={saving} className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60">
          {saving ? 'Saving...' : (mode === 'create' ? 'Create Committee' : 'Save Changes')}
        </button>
      )}
    </>
  );

  return (
    <RecordWizard
      steps={[
        { key: 'details', label: 'Committee Registration' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Fill in the details to create a new record' : 'Update the details for this record'}
      statusLabel={currentDataStatus}
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep(1)}
      canGoNext={canGoNext}
      nextLabel="Review"
      footer={isReviewStep ? footer : undefined}
      error={error}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Committee Registration" />
          <ReviewRow label="Committee Name" value={form.committeeName} />
          <ReviewRow label="Committee Type" value={form.committeeType} />
          <ReviewRow label="Chairperson" value={form.chairperson} />
          <ReviewRow label="Total Members" value={form.totalMembers} />
          <ReviewRow label="Trained Members" value={form.trainedMembers} />
          <ReviewRow label="Meeting Frequency" value={form.meetingFrequency} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="State" value={form.state} />
          <ReviewRow label="LGA" value={form.lga} />
          <ReviewRow label="Ward / Community" value={form.wardCommunity} />
          <ReviewRow label="Establishment Date" value={form.establishmentDate} />

          <SectionHead title="System" />
          <ReviewRow label="Status" value={form.status} />
        </>
      ) : (
        <div className="space-y-5">

          <section className="space-y-3">
            <SectionHead title="Committee Registration" />
            <div className="grid grid-cols-2 gap-4">
              <TextInput label="Committee Name" required value={form.committeeName} onChange={setValue('committeeName')} placeholder="Min 5 characters" />
              <Select label="Committee Type" required value={form.committeeType} onChange={setValue('committeeType')} options={COMMITTEE_TYPES} />
              <TextInput label="Chairperson" required value={form.chairperson} onChange={setValue('chairperson')} placeholder="Named accountability, min 3" />
              <TextInput label="Total Members" required type="number" value={form.totalMembers} onChange={setValue('totalMembers')} />
              <TextInput label="Trained Members" type="number" value={form.trainedMembers} onChange={setValue('trainedMembers')} hint="≤ Total Members" />
              <Select label="Meeting Frequency" required value={form.meetingFrequency} onChange={setValue('meetingFrequency')} options={MEETING_FREQUENCIES} />
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="Geospatial" />
            <div className="grid grid-cols-2 gap-4">
              <Select label="State" required value={form.state} onChange={setValue('state')} options={stateOptions} readOnly={Boolean(lockedState)} />
              <Select
                label="LGA"
                required
                value={form.lga}
                onChange={setValue('lga')}
                options={lgaOptions}
                readOnly={Boolean(lockedLga)}
                placeholder={form.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select state first'}
              />
              <Select
                label="Ward / Community"
                required
                value={form.wardCommunity}
                onChange={setValue('wardCommunity')}
                options={wardOptions}
                placeholder={form.lga ? (loadingWards ? 'Loading wards...' : 'Select Ward / Community') : 'Select LGA first'}
                readOnly={!form.lga || loadingWards}
              />
              <TextInput label="Establishment Date" required type="date" value={form.establishmentDate} onChange={setValue('establishmentDate')} max={todayISO()} hint="Cannot be in the future" />
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="System" />
            <div className="grid grid-cols-2 gap-4">
              <Select label="Status" required value={form.status} onChange={setValue('status')} options={STATUSES} />
            </div>
          </section>
        </div>
      )}
    </RecordWizard>
  );
}

function ReviewRow({ label: pageLabel, value }) {
  const label = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className="text-sm text-right text-gray-900">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}


export default function ConflictCommitteesTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? '';
  const isFieldUser = roleId === 'R04' || roleId === 'R05';
  const canManageCommittees = !['R02', 'R03', 'R06'].includes(roleId);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ committeeType: '', status: '', state: '', meetingFrequency: '' });
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);
  const { stateOptions } = useLocationOptions();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const committeeResult = await conflictsApi.committees.listAll({ per_page: 1000, page: 1 });
      setRecords(Array.isArray(committeeResult?.items) ? committeeResult.items : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to load conflict committees.';
      setError(msg);
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useCollectionSync(conflictsApi.committees.collectionKey, loadData);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((row) => {
      const searchable = [
        row.committeeName,
        row.committeeType,
        row.state,
        row.lga,
        row.wardCommunity,
        row.chairperson,
        row.meetingFrequency,
        row.status,
      ].filter(Boolean).join(' ').toLowerCase();

      return (
        (!q || searchable.includes(q)) &&
        (!filters.committeeType || row.committee_type === filters.committeeType) &&
        (!filters.status || String(row.status ?? '').toLowerCase() === filters.status) &&
        (!filters.state || row.state === filters.state) &&
        (!filters.meetingFrequency || row.meeting_frequency === filters.meetingFrequency)
      );
    });
  }, [records, filters, search]);

  const columns = useMemo(() => [
    { title: 'Committee Name', data: 'committeeName', sortable: true, defaultContent: '' },
    { title: 'Committee Type', data: 'committeeType', sortable: true, defaultContent: '' },
    { title: 'State', data: 'state', sortable: true, defaultContent: '' },
    { title: 'LGA', data: 'lga', sortable: true, defaultContent: '' },
    { title: 'Ward / Community', data: 'wardCommunity', sortable: true, defaultContent: '' },
    { title: 'Chairperson', data: 'chairperson', sortable: true, defaultContent: '' },
    { title: 'Meeting Frequency', data: 'meetingFrequency', sortable: true, defaultContent: '', render: badgeHtml },
    { title: 'Establishment Date', data: 'establishedDate', sortable: true, defaultContent: '', render: (data, type) => (type === 'display' ? formatDate(data) : (data ?? '')) },
    { title: 'Status', data: 'status', sortable: true, defaultContent: '', render: badgeHtml },
    { title: 'Total Members', data: 'totalMembers', sortable: true, defaultContent: '' },
    { title: 'Trained Members', data: 'trainedMembers', sortable: true, defaultContent: '' },
    { title: 'Action', data: null, sortable: false, defaultContent: '', render: () => actionCellHtml() },
  ], []);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);

  const runQuickAction = useCallback(async (row, method) => {
    setQuickBusy(true);
    try {
      await conflictsApi.committees[method](row.uuid);
      await loadData();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || `Failed to ${method} record.`;
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setQuickBusy(false);
    }
  }, [loadData]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy(true);
    try {
      await conflictsApi.committees.reject(rejectTarget.uuid, notes);
      await loadData();
      setRejectTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to reject record.';
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
      throw err;
    } finally {
      setQuickBusy(false);
    }
  }, [rejectTarget, loadData]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy(true);
    try {
      await conflictsApi.committees.remove(deleteTarget.uuid);
      await loadData();
      setDeleteTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete record.';
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setQuickBusy(false);
    }
  }, [deleteTarget, loadData]);

  if (error && records.length === 0 && !loading) {
    return (
      <section className="rounded-xl border border-red-100 bg-red-50 px-6 py-10 text-sm text-red-700">
        {error}
      </section>
    );
  }

  return (
    <div>
      <CommitteeStatCards committees={records} />
      <FilterBar
        filters={[
          { key: 'committeeType', label: 'Committee Type', options: COMMITTEE_TYPES, defaultLabel: 'All' },
          { key: 'status', label: 'Status', options: STATUSES, defaultLabel: 'All' },
          { key: 'state', label: 'State', options: stateOptions, defaultLabel: 'All' },
          { key: 'meetingFrequency', label: 'Meeting Frequency', options: MEETING_FREQUENCIES, defaultLabel: 'All' },
        ]}
        filterValues={filters}
        onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onApply={() => {}}
      />

      <SearchRow searchValue={search} onSearch={setSearch} onCreate={() => navigate('/conflicts-dispute/new?tab=committees')} canCreate={canManageCommittees} />

      {loading && records.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-sm text-gray-500">
          Loading conflict committees...
        </div>
      ) : (
        <NlimsDataTable
          tableId="committees-table"
          columns={columns}
          data={filtered}
          serverSide={false}
          hideSearch
          pageLength={10}
          enableRowSelection
          onRowAction={handleRowAction}
        />
      )}

      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.conflictCommittees}
          statusField="dataStatus"
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/conflicts-dispute/view/${actionMenu.row.uuid}?tab=committees`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/conflicts-dispute/edit/${actionMenu.row.uuid}?tab=committees`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => runQuickAction(actionMenu.row, 'submit')}
          onValidate={() => runQuickAction(actionMenu.row, 'validate')}
          onApprove={() => runQuickAction(actionMenu.row, 'approve')}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={(r) => r?.committeeName || ''}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={quickBusy}
        />
      )}

      {createOpen && (
        <CommitteeEditorModal
          mode="create"
          row={null}
          onClose={() => setCreateOpen(false)}
          onSaved={loadData}
          stateOptions={stateOptions}
          isFieldUser={isFieldUser}
        />
      )}
    </div>
  );
}
